import type Konva from 'konva';
import type { IRect } from 'konva/lib/types';
import { createContext, useContext, type ReactNode } from 'react';
import { assert } from '~/lib/assert';
import type { LinkName, SettingsTabName, StudioTransformerState } from './layout/utils';

export type Direction = 'back' | 'backward' | 'forward' | 'front';
export type Position = 'bottom' | 'center' | 'left' | 'middle' | 'right' | 'top';
export type LetterCase = 'capitalize' | 'lowercase' | 'uppercase';

export interface StudioUrlAction {
  method: 'GET' | 'POST';
  type: 'url';
  url: string;
}

// A union once there is more than one kind. Absent means the element does nothing on click.
export type StudioAction = StudioUrlAction;

export interface BaseStudioItem {
  // Underscored like __fontId: ours rather than Konva's, and items are spread onto the node.
  __action?: StudioAction;
  id: string;
  locked?: boolean;
  x: number;
  y: number;
}

export interface StudioCircle extends Konva.CircleConfig {
  type: 'circle';
}

export interface StudioRect extends Konva.RectConfig {
  type: 'rect';
}

export interface StudioTriangle extends Konva.RegularPolygonConfig {
  type: 'triangle';
}

// ideally this would extend Omit<Konva.ImageConfig, 'image'>
// but it doesn't like that for some reason
export interface StudioMedia extends Konva.ShapeConfig {
  __mediaItemId: number;
  // these are the other two properties from Konva.ImageConfig
  crop?: IRect;
  cornerRadius?: number | number[];
  format?: string;
  src: string;
}

export interface StudioImage extends StudioMedia {
  type: 'image';
}

export interface StudioVideo extends StudioMedia {
  type: 'video';
}

export interface StudioText extends Konva.TextConfig {
  __dataItemId?: number;
  // Set instead of __dataItemId when the playing device picks the row, on the source's device key.
  __deviceDataSourceId?: number;
  __dataItemKey?: string;
  __fontId?: number;
  __textTransform?: LetterCase;
  type: 'text';
}

export type StudioItem = (
  | StudioCircle
  | StudioRect
  | StudioTriangle
  | StudioImage
  | StudioVideo
  | StudioText
) &
  BaseStudioItem;

export interface StudioFontFamily {
  options?: {
    style: 'normal' | 'italic';
    weight: 'normal' | 'bold';
  };
  uri: string;
}

export interface StudioFont {
  __fontId?: number;
  family?: StudioFontFamily[];
  key: string;
  value: string;
}

export interface StudioContextType {
  activeSettingsTab: SettingsTabName;
  activeSidebarItem: LinkName;
  addItems: (items: StudioItem[]) => void;
  alignItem: (itemId: string, position: Position) => void;
  canRedo: boolean;
  canUndo: boolean;
  copiedItems: StudioItem[];
  copyItems: (ids: string[]) => void;
  dataItemCellSelectOpen: boolean;
  deleteItems: (ids: string[]) => void;
  dirty: boolean;
  duplicateItems: (items: StudioItem[]) => void;
  editItem: (id?: string) => void;
  editingItem?: StudioItem;
  fonts?: StudioFont[];
  items: StudioItem[];
  loadItems: (items: StudioItem[]) => void;
  loaded: boolean;
  moveItem: (itemId: string, direction: Direction) => void;
  /** Device whose values device-aware items show on the canvas. Never saved. */
  previewDeviceId: string;
  setPreviewDeviceId: (deviceId: string) => void;
  /** The previewed value for a device-aware item, or undefined. Display only. */
  previewTextFor: (item: StudioItem) => string | undefined;
  redo: () => void;
  toggleLockItems: (ids: string[]) => void;
  reorderItems: (ids: string[]) => void;
  resetDirty: () => void;
  selectItems: (ids: string[]) => void;
  selectedIds: Set<string>;
  selectedItems: StudioItem[];
  setActiveSettingsTab: (tab: SettingsTabName) => void;
  setDataItemCellSelectOpen: (open: boolean) => void;
  setFonts: (fonts: StudioFont[]) => void;
  setMessage: (message?: string) => void;
  setShowRulers: (show: boolean) => void;
  setSidebarItem: (name: LinkName) => void;
  showRulers: boolean;
  setStage: (stage: Konva.Stage) => void;
  setTransformer: (transformer: Partial<StudioTransformerState>) => void;
  stage?: Konva.Stage;
  transformer: StudioTransformerState;
  zoom: number;
  setZoom: (zoom: number) => void;
  undo: () => void;
  updateItems: (
    items: Array<Partial<Omit<StudioItem, 'type'>> & Required<Pick<StudioItem, 'id'>>>,
  ) => void;
}

export const StudioContext = createContext<StudioContextType | undefined>(undefined);

export const useStudio = () => {
  const context = useContext(StudioContext);
  assert(context !== undefined, 'useStudio must be used within a StudioProvider');
  return context;
};

export interface StudioProviderProps extends StudioContextType {
  children: ReactNode;
}

export const StudioProvider = ({ children, ...rest }: StudioProviderProps) => (
  <StudioContext.Provider value={{ ...rest }}>{children}</StudioContext.Provider>
);
